import React from 'react';
import { Zap, Upload, Wand2, ArrowRight } from 'lucide-react';
import Icon from '../../../components/AppIcon';


const features = [
  {
    icon: Zap,
    tag: 'Fastest',
    title: 'Quick List',
    subtitle: 'Single property in under 5 minutes',
    description: 'Upload one property fast with only the essential fields. Perfect for agents who need to get a listing live immediately with minimal friction.',
    highlights: ['5 required fields only', 'Auto-fill from address', 'Instant AI description', 'Live in minutes'],
    color: '#00D4FF',
    action: 'onQuickList'
  },
  {
    icon: Upload,
    tag: 'Most Efficient',
    title: 'Bulk Upload',
    subtitle: 'Upload entire portfolio at once',
    description: 'Import multiple properties via CSV or spreadsheet. Ideal for agencies managing large portfolios or developers launching new projects.',
    highlights: ['CSV / Excel import', 'Up to 500 properties', 'Batch AI enhancement', 'Auto-validation'],
    color: '#7B2FFF',
    action: 'onBulkUpload'
  },
  {
    icon: Wand2,
    tag: 'Most Complete',
    title: 'Full Wizard',
    subtitle: 'Comprehensive listing creation',
    description: 'Step-by-step guided workflow covering every detail — photos, pricing, AI copy, professional services, and multi-portal publishing.',
    highlights: ['8-step guided flow', 'AI copy generation', 'Price analysis', 'Multi-portal publish'],
    color: '#00FF88',
    action: 'onStartListing'
  }
];

const FeatureShowcase = ({ onStartListing, onQuickList, onBulkUpload }) => {
  const handlers = { onStartListing, onQuickList, onBulkUpload };

  return (
    <section className="py-20 px-4" id="features" style={{ background: 'rgba(0, 212, 255, 0.02)' }}>
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-14">
          <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ color: '#E8F4FD' }}>Choose Your Listing Method</h2>
          <p className="text-base" style={{ color: '#6B9EC4' }}>Three ways to get your property in front of buyers — pick what fits your workflow.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {features?.map((feature, i) => {
            const Icon = feature?.icon;
            const handler = handlers?.[feature?.action];
            return (
              <div
                key={i}
                className="p-7 rounded-2xl flex flex-col"
                style={{
                  background: 'rgba(5, 10, 20, 0.8)',
                  border: `1px solid ${feature?.color}30`,
                  boxShadow: `0 0 30px ${feature?.color}10`
                }}
              >
                <div className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold mb-5 self-start" style={{
                  background: `${feature?.color}20`,
                  color: feature?.color,
                  border: `1px solid ${feature?.color}40`
                }}>
                  {feature?.tag}
                </div>
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{
                    background: `${feature?.color}20`,
                    border: `1px solid ${feature?.color}40`
                  }}>
                    <Icon size={20} color={feature?.color} />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold" style={{ color: '#E8F4FD' }}>{feature?.title}</h3>
                    <p className="text-xs" style={{ color: '#4A7A9B' }}>{feature?.subtitle}</p>
                  </div>
                </div>
                <p className="text-sm leading-relaxed mb-5" style={{ color: '#6B9EC4' }}>{feature?.description}</p>
                <ul className="space-y-2 mb-6 flex-1">
                  {feature?.highlights?.map((h, j) => (
                    <li key={j} className="flex items-center gap-2 text-sm" style={{ color: '#A0C4D8' }}>
                      <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: feature?.color }} />
                      {h}
                    </li>
                  ))}
                </ul>
                <button
                  onClick={handler}
                  className="flex items-center justify-center gap-2 w-full py-3 rounded-xl text-sm font-semibold transition-all"
                  style={{ background: `${feature?.color}20`, color: feature?.color, border: `1px solid ${feature?.color}40` }}
                  onMouseEnter={e => { e.currentTarget.style.background = `${feature?.color}35`; }}
                  onMouseLeave={e => { e.currentTarget.style.background = `${feature?.color}20`; }}
                >
                  Get Started
                  <ArrowRight size={15} />
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default FeatureShowcase;
